import React, { useState, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useField, ErrorMessage } from "formik";
import {
  FormControl,
  Select,
  MenuItem,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  Box,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import FormikLabel from "./FormikLabel";
import { styled } from "@mui/material/styles";
import { ErrorStyle } from "./ErrorStyle";
import KeyboardArrowDownSharpIcon from "@mui/icons-material/KeyboardArrowDownSharp";
import SearchIcon from "@mui/icons-material/Search";

// Styled Select Input
const CustomSelect = styled(Select)(({ disabled }) => ({
  display: "flex",
  alignItems: "flex-start",
  padding: "0 !important",
  minHeight: "40px",
  cursor: disabled ? "not-allowed" : "pointer",
  "& .MuiSelect-select": {
    display: "flex",
    flexWrap: "wrap",
    maxHeight: "80px",
    overflowY: "auto",
    overflowX: "hidden",
    alignItems: "flex-start",
    gap: "4px",
    padding: "8px 8px !important",
    backgroundColor: disabled ? "#f5f5f5" : "inherit",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#ccc",
      borderRadius: "2px",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "8px",
  },
}));

const CustomChip = styled(Chip)(() => ({
  height: "24px",
  margin: "2px",
  fontSize: "0.75rem",
}));

const FormikMultipleSelectChip = ({
  name = "",
  label = "",
  options = [],
  placeholder = "",
  required = false,
  disabled = false,
  maxSelect,
  width = "100%",
  onChange = () => {},
  search = false,
}) => {
  const [field, meta, helpers] = useField(name);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuPlacement, setMenuPlacement] = useState("bottom");
  const selectRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    return options.filter((option) => {
      const label =
        typeof option === "string"
          ? option
          : option.label
          ? option.label
          : option.name;

      return (
        typeof label === "string" &&
        label.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [search, options, searchTerm]);

  const handleChange = (event) => {
    const value = event.target.value;
    const newValue = typeof value === "string" ? value.split(",") : value;
    helpers.setValue(newValue);
    onChange(event);
  };

  const handleDelete = (chipToDelete) => () => {
    const newValue = field.value.filter((value) => value !== chipToDelete);
    helpers.setValue(newValue);
    onChange({ target: { value: newValue } });
  };

  const handleOpen = () => {
    const rect = selectRef.current?.getBoundingClientRect?.();
    if (!rect) return;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    setMenuPlacement(
      spaceBelow < 200 && spaceAbove > spaceBelow ? "top" : "bottom"
    );
  };

  const MenuProps = {
    disableAutoFocusItem: true,
    disablePortal: true,
    PaperProps: {
      style: {
        maxHeight: 200,
        zIndex: 1300,
        marginTop: menuPlacement === "bottom" ? 4 : 0,
        marginBottom: menuPlacement === "top" ? 4 : 0, // spacing when opening upward
      },
      onMouseDown: (e) => e.stopPropagation(),
    },
    anchorOrigin: {
      vertical: menuPlacement,
      horizontal: "left",
    },
    transformOrigin: {
      vertical: menuPlacement === "bottom" ? "top" : "bottom",
      horizontal: "left",
    },
  };

  return (
    <FormControl variant="outlined" fullWidth sx={{ width }} ref={selectRef}>
      <FormikLabel text={label} required={required} />
      <CustomSelect
        {...field}
        multiple
        value={field.value || []}
        onChange={handleChange}
        onOpen={handleOpen}
        displayEmpty
        disabled={disabled}
        IconComponent={disabled ? null : KeyboardArrowDownSharpIcon}
        MenuProps={MenuProps}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return (
              <Typography sx={{ color: "rgba(0, 0, 0, 0.54)" }}>
                {placeholder}
              </Typography>
            );
          }

          return selected.map((value) => (
            <CustomChip
              key={value}
              label={options.find((opt) => opt.value === value)?.label}
              deleteIcon={
                disabled? null:
                <CancelIcon onMouseDown={(e) => e.stopPropagation()} />
              }
              onDelete={handleDelete(value)}
            />
          ));
        }}
      >
        {/* Search input if enabled */}
        {search && (
          <MenuItem disableRipple disableTouchRipple>
            <TextField
              fullWidth
              placeholder="Search..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </MenuItem>
        )}

        {/* Placeholder */}
        <MenuItem disabled value="">
          {placeholder}
        </MenuItem>

        {/* Render options */}
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            const isSelected = field.value?.includes(option.value);
            const isDisabled = !isSelected && field.value?.length >= maxSelect;
            const optionValue =
              typeof option === "string" ? option : option.value ?? option.name;

            const optionLabel =
              typeof option === "string" ? option : option.label ?? option.name;

            return (
              <MenuItem
                key={optionValue}
                value={optionValue}
                disabled={isDisabled}
              >
                {optionLabel}
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>No results found</MenuItem>
        )}
      </CustomSelect>

      <div style={ErrorStyle}>
        <ErrorMessage name={name} />
      </div>
    </FormControl>
  );
};

FormikMultipleSelectChip.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  width: PropTypes.string,
  onChange: PropTypes.func,
  maxSelect: PropTypes.number,
  search: PropTypes.bool,
};

export default FormikMultipleSelectChip;
